import { Link } from "react-router-dom";
import { Github, MessageCircle, BookOpen, Users, Video, Award } from "lucide-react";

const channels = [
  { icon: MessageCircle, title: "Discord", text: "Chat with the community and the Eamico team. Get help in real-time.", cta: "Join Discord", color: "#5865F2" },
  { icon: Github, title: "GitHub", text: "Report issues, contribute to open source, and browse our public repos.", cta: "View GitHub", color: "#24292e" },
  { icon: BookOpen, title: "Forum", text: "Ask questions, share knowledge, and discuss best practices.", cta: "Visit Forum", color: "#ff9a4d" },
];

const resources = [
  { icon: Video, title: "Tutorials", text: "Step-by-step video guides to get the most out of Eamico." },
  { icon: Users, title: "Meetups", text: "Join local and virtual meetups with other Eamico developers." },
  { icon: Award, title: "Champions Program", text: "Become an Eamico Champion. Get exclusive perks and early access." },
];

const CommunityPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="community-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase mb-4">Community</p>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="community-title">
              Build together. Ship together.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed max-w-xl">
              Join thousands of developers who are building the future of cloud computing with Eamico.
            </p>
          </div>
        </div>
      </div>

      {/* Channels */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">Where to find us</h2>
        <div className="grid sm:grid-cols-3 gap-6 lg:gap-8" data-testid="community-channels">
          {channels.map((ch, i) => {
            const Icon = ch.icon;
            return (
              <Link key={i} to="#" className="group block p-8 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-md transition-all text-center" data-testid={`channel-${i}`}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${ch.color}12` }}>
                  <Icon className="w-7 h-7" style={{ color: ch.color }} />
                </div>
                <h3 className="text-[18px] font-medium text-[#202124] mb-2">{ch.title}</h3>
                <p className="text-[14px] text-[#5f6368] leading-relaxed mb-5">{ch.text}</p>
                <span className="inline-flex items-center text-[14px] font-medium text-[#ff9a4d] group-hover:text-[#e8872e] transition-colors">
                  {ch.cta}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Resources */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">Resources</h2>
          <div className="grid sm:grid-cols-3 gap-6" data-testid="community-resources">
            {resources.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className="p-6 bg-white rounded-xl border border-[#e8eaed]" data-testid={`resource-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#ff9a4d]" />
                  </div>
                  <h3 className="text-[15px] font-medium text-[#202124] mb-1.5">{r.title}</h3>
                  <p className="text-[13px] text-[#5f6368] leading-relaxed">{r.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Community stat */}
      <div className="border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-3 gap-8 text-center max-w-xl mx-auto">
            {[
              { num: "5,000+", label: "Developers" },
              { num: "12,000+", label: "Discord members" },
              { num: "200+", label: "Contributors" },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-[28px] sm:text-[32px] font-normal text-[#202124]">{s.num}</p>
                <p className="text-[12px] text-[#5f6368] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityPage;
